import { Grid, styled } from "@mui/material";
import { AllowedFileExtensions } from "@rf-l1/file/model/types.model";
import React from "react";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import MxFieldFooterDescription from "./FooterDescription";
import EmptyPlaceHolderCarrusel from "./EmptyPlaceHolderCarrusel";
import { RomaProvider, useRoma } from "@rf-l1/roma/RomaContext";
import { RomaExtras } from "@rf-l1/roma/models/Roma.model";

export interface MxFieldCarruselProps {
  value: IFileData[];
  onChange: (
    value: { target: { name: string; value: IFileData[] } },
    extras?: RomaExtras
  ) => void;
  accept?: AllowedFileExtensions[];
  maxSize?: number;
  minSize?: number;
  direction?: "horizontal" | "vertical";
  itemRender?: (item: IFileData, index: number) => React.ReactNode;
  label?: string;
  height?: string;
  id?: string;
  name?: string;
  uploadMethod?: (data: IFileData) => Promise<void>;
  onAfterDeleteAllFiles?: (
    current: IFileData[],
    api: { set: (files: IFileData[]) => void }
  ) => void | Promise<void>;
}

const MxFieldCarrusel = (props: MxFieldCarruselProps) => {
  const {
    value,
    onChange,
    accept,
    maxSize,
    minSize,
    direction,
    itemRender,
    height,
    id,
    uploadMethod,
    onAfterDeleteAllFiles
  } = props;

  const handleChange = (value: IFileData[], extras?: RomaExtras) => {
    onChange({ target: { name: props.name ?? "", value } }, extras);
  };
  return (
    <RomaProvider
      initialState={value}
      accept={accept}
      maxSize={maxSize}
      minSize={minSize}
      isMultiple={true}
      onDataChange={handleChange}
      direction={direction}
      uploadMethod={uploadMethod}
      onAfterDeleteAllFiles={onAfterDeleteAllFiles}
    >
      <CarruselImages itemRender={itemRender} height={height} id={id} />
    </RomaProvider>
  );
};

const CarruselImages = (props: {
  itemRender?: (item: IFileData, index: number) => React.ReactNode;
  height?: string;
  id?: string;
}) => {
  const {
    getRootProps,
    getInputProps,
    data = [],
    getContentSortableProps,
    getSortableItemProps
  } = useRoma();

  // Ensure we always work with an array to avoid runtime errors
  const list = Array.isArray(data) ? data : [];

  return (
    <ContentCarrusel
      container
      item
      xs={12}
      {...getRootProps()}
      height={props.height}
      data-testid={`${props.id}-carrusel-images`}
    >
      <input {...getInputProps()} />

      {list.length === 0 && <EmptyPlaceHolderCarrusel />}

      <Grid
        container
        item
        xs={12}
        gap={2}
        my={4}
        {...getContentSortableProps()}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          flexWrap: "nowrap",
          flexDirection: "row"
        }}
        data-testid={`${props.id}-carrusel-images-content`}
      >
        {list.map((item, index) => (
          <Grid
            item
            sx={{ width: "auto", height: "auto" }}
            key={item?.id}
            {...getSortableItemProps(index)}
            data-testid={`${props.id}-carrusel-images-content-item`}
          >
            {props.itemRender
              ? props.itemRender({ ...item } as IFileData, index)
              : null}
          </Grid>
        ))}
      </Grid>

      <MxFieldFooterDescription />
    </ContentCarrusel>
  );
};

const ContentCarrusel = styled(Grid)<{
  height?: string;
}>(({ height }) => ({
  border: "1px dashed #ccc",
  borderRadius: "8px",
  padding: "16px",
  cursor: "pointer",
  minHeight: height
}));

export default MxFieldCarrusel;
